import { useEffect, useMemo, useRef, useState } from "react";
import { CARD_PIXEL_SIZE, GRID_SIZE } from "../../constants/editor";
import { composeCardGrid } from "../../lib/card-scene";
import { getFrameColor, getFrameWeightPx } from "../../lib/card";
import type { CardDraft } from "../../shared/game";
import { PixelArtCanvas } from "../canvas/PixelArtCanvas";
import { FrameOverlay } from "./FrameOverlay";

type StaticCardProps = {
  card: CardDraft;
  scale?: number;
};

export function StaticCard({ card, scale = 1 }: StaticCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardSize, setCardSize] = useState<[number, number]>([386, 580]);
  const frameColor = getFrameColor(card);
  const frameWeightPx = getFrameWeightPx(card);
  const typeColor = getFrameColor({ ...card, frameColorManual: null });
  const composedGrid = useMemo(() => composeCardGrid(card), [card]);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) {
      return;
    }
    const update = () => {
      const bounds = element.getBoundingClientRect();
      setCardSize([bounds.width, bounds.height]);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pixelCanvasSize = useMemo(() => GRID_SIZE * CARD_PIXEL_SIZE * scale, [scale]);

  return (
    <div
      ref={cardRef}
      style={{
        width: pixelCanvasSize + 32 * scale,
        background: "linear-gradient(160deg,#14102a 0%,#09070f 55%,#0c1120 100%)",
        border: `${frameWeightPx}px solid ${frameColor}`,
        borderRadius: 22 * scale,
        padding: 14 * scale,
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 0 0 1px ${frameColor}25, 0 14px 44px rgba(0,0,0,0.55)`,
      }}
    >
      <div style={{ position: "absolute", inset: 0, borderRadius: 20 * scale, background: `linear-gradient(120deg,${frameColor}0d 0%,transparent 30%,${frameColor}08 52%,transparent 70%,${frameColor}0d 100%)`, pointerEvents: "none" }} />
      <FrameOverlay frameStyle={card.frameStyle} color={frameColor} width={cardSize[0]} height={cardSize[1]} />
      <div style={{ position: "relative", zIndex: 2, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 * scale, gap: 12 * scale }}>
        <div style={{ color: "#fff", fontSize: 16 * scale, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{card.name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 * scale, flexShrink: 0 }}>
          <div style={{ width: 8 * scale, height: 8 * scale, borderRadius: "50%", background: typeColor, boxShadow: `0 0 8px ${typeColor}` }} />
          <div style={{ padding: `${4 * scale}px ${8 * scale}px`, borderRadius: 8 * scale, border: `1px solid ${typeColor}77`, color: typeColor, background: `${typeColor}18`, fontSize: 10 * scale, fontWeight: 700 }}>
            HP {card.hp}
          </div>
        </div>
      </div>
      <div style={{ position: "relative", zIndex: 2, borderRadius: 12 * scale, overflow: "hidden", border: `2px solid ${typeColor}33` }}>
        <PixelArtCanvas grid={composedGrid} size={pixelCanvasSize} />
      </div>
      <div style={{ position: "relative", zIndex: 2, marginTop: 10 * scale, color: "#c3cad8", fontSize: 12 * scale, lineHeight: 1.5 }}>
        {card.description}
      </div>
    </div>
  );
}
